/**
 * What a refused or unrecognised paste says (split out of `pastePreview.ts`, which re-exports it): the rules' reason
 * (`ConnectionStringParseResult.refusal`), else the formats the paste reads. Fixed text and keyword names only — never a
 * value or a piece of the pasted string.
 */
import type { ConnectionStringParseResult, ReadingFormat } from '../../services/connection-string/parseConnectionString';
import type { MssqlTlsKeyword } from '@/core/engines/mssqlEncrypt';

export const PASTE_NOT_RECOGNIZED = 'Couldn’t recognize this connection string.';
export const PASTE_FORMATS_LINE =
  'Paste an ADO.NET, Kusto, URL, JDBC, EZConnect or libpq string, an Azure Storage connection string or URL, or a SQLite file path.';
export const PASTE_FAILED_MESSAGE = `${PASTE_NOT_RECOGNIZED} ${PASTE_FORMATS_LINE}`;

const READING_LABELS: Record<ReadingFormat, string> = {
  libpq: 'PostgreSQL (libpq)',
  adonet: 'SQL Server (ADO.NET)',
  odbc: 'SQL Server (ODBC)',
  npgsql: 'PostgreSQL (Npgsql)',
  mysqlconnector: 'MySQL (MySqlConnector)',
  kusto: 'Azure Data Explorer (Kusto)',
  'azure-storage': 'Azure Storage',
};

/** Two valid readings that disagree about a secret (C3 review r5, C3-21): refused in every context. */
export function pasteConflictMessage([first, second]: [ReadingFormat, ReadingFormat]): string {
  return `This string reads differently as ${READING_LABELS[first]} and ${READING_LABELS[second]} — choose the engine and paste the password separately.`;
}

/** A quoted value that never closes (C3 review r5): the string is refused rather than read some other way. */
export const PASTE_UNCLOSED_MESSAGE = 'A quoted value in this string isn’t closed — check its quotes, or paste the password separately.';

const ANNOUNCED_LABELS: Record<'url' | 'jdbc' | 'ezconnect', string> = { url: 'a connection URL', jdbc: 'JDBC', ezconnect: 'Oracle EZConnect' };

/** A value opening with a quote its format does not honour (the r5 ruling): refused, naming the character. */
export function pasteUnhonouredQuoteMessage({ format, quote }: NonNullable<ConnectionStringParseResult['unhonouredQuote']>): string {
  const label = format in READING_LABELS ? READING_LABELS[format as ReadingFormat] : ANNOUNCED_LABELS[format as keyof typeof ANNOUNCED_LABELS];
  return `A value starts with ${quote}, which ${label} doesn’t treat as quoting — check the string, or type that value into the form.`;
}

/** "Encrypt", "Encrypt and Address / Server". */
function settingsList(groups: readonly string[][]): string {
  const names = groups.map((keys) => keys.join(' / '));
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Two readings for one engine that fill a different target or TLS setting (review round 5): refused, naming the keywords
 * the string repeats — ODBC uses a repeated keyword's first occurrence, SqlClient its last.
 */
export function pasteRepeatedKeywordMessage([first, second]: [ReadingFormat, ReadingFormat], groups: readonly string[][] = []): string {
  if (groups.length === 0) {
    return `This string reads differently as ${READING_LABELS[first]} and ${READING_LABELS[second]} — check the string, or type the values into the form.`;
  }
  return `This string gives ${settingsList(groups)} more than once, and ${READING_LABELS[first]} and ${READING_LABELS[second]} use different ones — keep one and paste again.`;
}

const ACCEPTED_TLS: Readonly<Record<MssqlTlsKeyword, string>> = { Encrypt: 'yes, no or strict', TrustServerCertificate: 'yes or no' };

/** An Encrypt / TrustServerCertificate value SQL Server's drivers reject (review round 5): refused, naming the keyword. */
export function pasteUnreadableValueMessage(keyword: MssqlTlsKeyword): string {
  return `${keyword} has a value SQL Server doesn’t accept — set it to ${ACCEPTED_TLS[keyword]} and paste again.`;
}

/** The parts of a failed parse its message is built from. */
export type PasteRefusal = Pick<ConnectionStringParseResult, 'refusal' | 'conflict' | 'unhonouredQuote' | 'repeatedKeywords' | 'unreadable'>;

/** What a failed parse says: why the rules refused it, else the formats it reads. */
export function pasteFailedMessage(result: PasteRefusal | null | undefined): string {
  if (result?.refusal === 'conflict' && result.conflict) return pasteConflictMessage(result.conflict);
  if (result?.refusal === 'repeated-keyword' && result.conflict) return pasteRepeatedKeywordMessage(result.conflict, result.repeatedKeywords);
  if (result?.refusal === 'unreadable-value' && result.unreadable) return pasteUnreadableValueMessage(result.unreadable);
  if (result?.refusal === 'unhonoured-quote' && result.unhonouredQuote) return pasteUnhonouredQuoteMessage(result.unhonouredQuote);
  return result?.refusal === 'unclosed' ? PASTE_UNCLOSED_MESSAGE : PASTE_FAILED_MESSAGE;
}
